import type {
  ArtDetailSerializer,
  IApiResponse,
  IApiResponseMeta,
  ArtFileTypeEnum,
  RecommendationDataPaginationResponse_ArtShortSerializer_,
  RecommendationPaginationResponse_ArtShortSerializer_,
  ArtQuoteSerializer,
  IResponse,
  UserReferralLink,
  ComplaintReasonSerializer,
  OkResponse_ArtComplaintSerializer_,
  ArtOnlineReadDataSerializer,
  ArtFileWithEncodingType,
  PodcastEpisodeSerializer,
  BookAudiobookBundleSerializer,
  WebtoonChaptersSerializer,
  CollectionWithRubricsSerializer,
} from "@litres-libs/types";
import type {
  ArtDRM,
  ArtTableContentXml,
  GetArtQuotesRequestParams,
  IApiResponseWithTotalCounts,
  IMegafonByClick,
  IOftenSearched,
} from "./api.types";

import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

export const artApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getArtData: build.query<
      ArtDetailSerializer & ArtDRM,
      {
        artIdOrSlug: number | string;
      }
    >({
      query: ({ artIdOrSlug }) => ({
        url: `/foundation/api/arts/${artIdOrSlug}`,
      }),
      transformResponse: (
        response: IApiResponse<ArtDetailSerializer & ArtDRM>,
        meta: IApiResponseMeta
      ) => {
        const drm3Key =
          meta.headers["Audio_drm3_key"] || meta.headers["audio_drm3_key"];
        return {
          ...response.payload.data,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          audio_drm3_key: drm3Key || null,
        };
      },
      providesTags: result => {
        // инвалидируем кэш конкретного арта
        if (result !== undefined) {
          return [{ type: TagsType.ART, id: result.id }];
        }

        // инвалидируем  информацию об арте вне зависимости от id
        return [{ type: TagsType.ART, id: "ART" }];
      },
    }),

    getArtFiles: build.query<ArtFileWithEncodingType[], { artId: number }>({
      query: ({ artId }) => ({
        url: `/foundation/api/arts/${artId}/files/grouped`,
      }),
      transformResponse: (
        response: IApiResponse<
          { file_type: ArtFileTypeEnum; files: ArtFileWithEncodingType[] }[]
        >
      ) => {
        return (
          response.payload.data?.reduce(
            (acc: ArtFileWithEncodingType[], val) => [
              ...acc,
              ...val.files.map(item => ({
                ...item,
                encoding_type: val.file_type,
              })),
            ],
            []
          ) || []
        );
      },
    }),

    getArtPersonalRecommendation: build.query<
      RecommendationDataPaginationResponse_ArtShortSerializer_,
      { SID?: string; artGroup?: number; limit?: number }
    >({
      query: ({ SID, artGroup, limit }) => ({
        url: `/foundation/api/arts/personal-recommendations?art_group=${
          artGroup ?? 0
        }&limit=${limit ?? 10}`,
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (
        response: RecommendationPaginationResponse_ArtShortSerializer_
      ) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),

    getSimilarArtsForRecommendation: build.query<
      RecommendationDataPaginationResponse_ArtShortSerializer_,
      {
        SID?: string;
        artIDorSlug: number | string;
        limit?: number | string;
        offset?: number | string;
      }
    >({
      query: ({ SID, artIDorSlug, limit = 24, offset = 0 }) => ({
        url: `/foundation/api/arts/${artIDorSlug}/similar`,
        params: { limit, offset },
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (
        response: RecommendationPaginationResponse_ArtShortSerializer_
      ) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),

    getArtQuotes: build.query<
      IApiResponseWithTotalCounts<ArtQuoteSerializer[]>,
      GetArtQuotesRequestParams
    >({
      query: ({ artId, url = "", limit, SID, offset }) => {
        let purifiedUrl = url;
        if (url) {
          const [pathname, params] = url.split("?");
          if (params) {
            const urlParams = new URLSearchParams(params);

            if (limit) urlParams.delete("limit");
            if (offset) urlParams.delete("offset");

            purifiedUrl = `${pathname}?${urlParams.toString()}`;
          }
        }

        return {
          url: purifiedUrl
            ? `/foundation/${purifiedUrl}`
            : `/foundation/api/arts/${artId}/quotes`,
          params: { limit, ...(offset && { offset }) },
          headers: SID
            ? {
                "Session-Id": SID,
              }
            : undefined,
        };
      },
      providesTags: result => {
        return result
          ? [
              ...result.payload.data.map(({ uuid }: ArtQuoteSerializer) => ({
                type: TagsType.QUOTE as const,
                id: uuid.split("-").join(""),
              })),
            ]
          : [{ type: TagsType.QUOTE, id: "QUOTE_LIST" }];
      },
    }),

    addArtRating: build.mutation<
      IResponse,
      { artId: number; rating: number; artIdOrSlug?: number | string }
    >({
      query: ({ artId, rating }) => ({
        url: `/foundation/api/arts/${artId}/rating`,
        method: "PUT",
        body: { rating },
      }),
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          artApi.util.updateQueryData(
            "getArtData",
            { artIdOrSlug: params.artIdOrSlug || params.artId },
            draft => {
              Object.assign(draft, {
                rating: {
                  ...draft.rating,
                  user_rating: params.rating,
                  ...(draft.rating?.rated_total_count &&
                  !draft.rating.user_rating
                    ? { rated_total_count: draft.rating.rated_total_count + 1 }
                    : {}),
                },
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_res, _err, { artId }) => [
        { type: TagsType.ART, id: artId },
      ],
    }),

    deleteArtRating: build.mutation<
      IResponse,
      { artId: number; artIdOrSlug?: number | string }
    >({
      query: ({ artId }) => ({
        url: `/foundation/api/arts/${artId}/rating`,
        method: "DELETE",
      }),
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          artApi.util.updateQueryData(
            "getArtData",
            { artIdOrSlug: params.artIdOrSlug || params.artId },
            draft => {
              Object.assign(draft, {
                rating: {
                  ...draft.rating,
                  user_rating: 0,
                  ...(draft.rating?.rated_total_count
                    ? { rated_total_count: draft.rating.rated_total_count - 1 }
                    : {}),
                },
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_res, _err, { artId }) => [
        { type: TagsType.ART, id: artId },
      ],
    }),

    getPodcastEpisodes: build.query<
      IApiResponse<PodcastEpisodeSerializer[]>,
      {
        podcastId: number;
        limit?: number;
        SID?: string;
        url?: string;
        with_episode_id?: number;
      }
    >({
      query: ({ podcastId, limit = 10, SID, url = "", with_episode_id }) => ({
        url: url
          ? `/foundation/${url}`
          : `/foundation/api/podcasts/${podcastId}/episodes`,
        params: { with_episode_id, limit },
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
    }),

    getArtTableContent: build.query<ArtTableContentXml, { url: string }>({
      query: ({ url }) => ({
        url: url,
        method: "GET",
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
    }),

    getWebtoonArtTableContent: build.query<
      WebtoonChaptersSerializer[],
      { artIdOrSlug: number | string; limit?: number }
    >({
      query: ({ artIdOrSlug, limit = 10 }) => ({
        url: `/foundation/api/webtoons/${artIdOrSlug}/chapters?limit=${limit}`,
        method: "GET",
      }),
      transformResponse: (
        response: IApiResponse<WebtoonChaptersSerializer[]>
      ) => {
        return response.payload.data;
      },
    }),

    getArtUrlReferral: build.query<
      UserReferralLink,
      {
        SID?: string;
        artId: number;
      }
    >({
      query: ({ SID, artId }) => ({
        url: `/foundation/api/users/referral/${artId}`,
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (response: IApiResponse<UserReferralLink>) => {
        return response.payload.data;
      },
    }),

    getReasonsComplaint: build.query<
      IApiResponse<ComplaintReasonSerializer[]>,
      void
    >({
      query: () => ({
        url: "/foundation/api/arts/complaints/reasons",
        method: "GET",
      }),
    }),

    getMegafonByClick: build.query<
      IApiResponse<IMegafonByClick>,
      { artId: number }
    >({
      query: ({ artId }) => ({
        url: `/foundation/api/arts/${artId}/megafon-one-click`,
      }),
    }),

    sendArtComplaint: build.mutation<
      OkResponse_ArtComplaintSerializer_,
      { artId: number; reason_ids: number[]; text: string; email: string }
    >({
      query: ({ artId, reason_ids, text, email }) => ({
        url: `/foundation/api/arts/${artId}/complain`,
        method: "POST",
        body: { reason_ids, text, email },
      }),
    }),

    getArtPages: build.query<
      ArtOnlineReadDataSerializer,
      { artIdOrSlug: number | string; page?: number }
    >({
      query: ({ artIdOrSlug, page = 1 }) => ({
        url: `/foundation/api/arts/${artIdOrSlug}/pages?page=${page}`,
      }),
      transformResponse: (
        response: IApiResponse<ArtOnlineReadDataSerializer>
      ) => {
        return response.payload.data;
      },
    }),

    getOftenSearched: build.query<IOftenSearched[], { itemId: number }>({
      query: ({ itemId }) => ({
        url: `/foundation/api/arts/often-searched?item_id=${itemId}`,
      }),
      transformResponse: (response: IApiResponse<IOftenSearched[]>) => {
        return response.payload.data;
      },
    }),
    getArtBundle: build.query<
      BookAudiobookBundleSerializer,
      { artIdOrSlug: number }
    >({
      query: ({ artIdOrSlug }) => ({
        url: `/foundation/api/arts/${artIdOrSlug}/bundle`,
      }),
      transformResponse: (
        response: IApiResponse<BookAudiobookBundleSerializer>
      ) => {
        return response.payload.data;
      },
    }),
    getArtJournalCollection: build.query<
      CollectionWithRubricsSerializer[],
      { artIdOrSlug: number | string }
    >({
      query: ({ artIdOrSlug }) => ({
        url: `/foundation/api/arts/${artIdOrSlug}/collections`,
      }),
      transformResponse: (
        response: IApiResponse<CollectionWithRubricsSerializer[]>
      ) => {
        return response.payload.data;
      },
    }),
  }),
});
